/**
 * "Upcoming" — `client/dashboard.html` §4 and the report document's own section.
 *
 * `readmodels/client-upcoming.ts` decides what is in the window and in what order;
 * this decides only how many are above the fold. DESIGN.md P3: "An 8-item timeline
 * → the next 3–4; 'the rest of the quarter →'." The mockup shows four and folds
 * four, and `VISIBLE_ITEMS` is that four in one place.
 *
 * **The link is data, not markup.** Each item carries `{ kind, ref }`
 * (`ClientUpcomingLinkSchema`) and `LINK_PATH` below is the one mapping from a kind
 * to a route — so a page never writes `to="/app/files"` beside a timeline row and the
 * six kinds cannot drift between the dashboard and the report.
 *
 * **The time is the viewer's.** `at` is an instant and `on` is a day; an item with
 * an `at` prints "Thu 3 Sep · 08:00 EDT" through `useFormat()`, which reads the
 * browser's zone (`ARCHITECTURE.md` §Frontend). An item that is only a day — an
 * invoice, a content drop — prints the day and no time, which is why `at` is
 * nullable rather than midnight.
 */

import type { ClientUpcomingItemView, ClientUpcomingLinkKind } from '@fareast/shared'
import { Link } from '@tanstack/react-router'

import { Empty, Fold } from '../portal/Disclosure.js'
import { Linkish } from '../portal/Text.js'
import { Timeline, TimelineItem } from '../portal/Timeline.js'
import { usePrincipal } from '../../auth/principal.js'
import { useFormat } from '../../format/useFormat.js'
import { abilitiesOf } from '../../pages/client/settings/roles.js'

/** How many rows sit above the fold. DESIGN.md P3's "next 3–4". */
export const VISIBLE_ITEMS = 4

/** What each link kind is called on the page it points at. */
const LINK_TEXT: Readonly<Record<ClientUpcomingLinkKind, string>> = {
  files: 'deliverables',
  reports: 'the archive',
  billing: 'services & billing',
  calls: 'reschedule',
  projects: 'project',
  requests: 'the request',
}

/**
 * Where a kind points. The one mapping; a page never writes one of these paths.
 *
 * The values are literals so `Link to=` keeps the router's own type checking — a
 * computed string would type as `string` and every one of these six would stop being
 * verified against the route tree.
 */
const LINK_PATH = {
  files: '/app/files',
  reports: '/app/reports',
  billing: '/app/billing',
  calls: '/app/calls',
  projects: '/app/projects',
  requests: '/app/requests',
} as const satisfies Record<ClientUpcomingLinkKind, string>

/**
 * The three destinations a `billing` seat is refused, as kinds rather than as paths.
 *
 * `.docs/19` §Edge cases takes requests, projects and files from a billing seat, and
 * `raisesRequests` is the ability that says so — the same column `RequestsPage`,
 * `ProjectsPage` and `FilesPage` each refuse on, and the same one the nav filters
 * with. A `billing` reader's `/app` was rendering twenty-eight "deliverables" links
 * into `/app/files`, every one of them landing on `RoleRefusal`.
 *
 * The **row** stays either way: "Video edits, Thu 27 Aug" is a fact about the account
 * and a billing reader is entitled to it. What goes is the promise that they can go
 * and look, which is `.docs/24`'s rule that a control shown to somebody the API will
 * refuse is worse than no control.
 */
const NEEDS_REQUESTS: readonly ClientUpcomingLinkKind[] = ['files', 'projects', 'requests']

/**
 * The anchor an item points at on its own page, spread rather than passed.
 *
 * `exactOptionalPropertyTypes` is on, so `hash={undefined}` is not the same as no
 * `hash` at all and the router's own props refuse it. Spreading `{}` is.
 */
export function hashFor(link: ClientUpcomingItemView['link']): { hash?: string } {
  return link.ref === null ? {} : { hash: link.ref.toLowerCase() }
}

export type UpcomingTimelineProps = {
  readonly items: readonly ClientUpcomingItemView[]
  /** How many before the fold. Pass `Infinity` for a document that folds nothing. */
  readonly visible?: number
  /** The line on the fold: "4 more, through 4 Oct". */
  readonly foldSummary?: string
  /** What to say when the window is empty. */
  readonly emptyLine?: string
}

export function UpcomingTimeline({
  items,
  visible,
  foldSummary,
  emptyLine,
}: UpcomingTimelineProps) {
  const limit = visible ?? VISIBLE_ITEMS
  const above = items.slice(0, limit)
  const below = items.slice(limit)

  if (items.length === 0) {
    return <Empty>{emptyLine ?? 'Nothing is scheduled in the next six weeks.'}</Empty>
  }

  return (
    <>
      <Timeline>
        {above.map((item) => (
          <Row key={rowKey(item)} item={item} />
        ))}
      </Timeline>

      {below.length > 0 ? (
        <Fold summary={foldSummary ?? `${String(below.length)} more`}>
          <Timeline>
            {below.map((item) => (
              <Row key={rowKey(item)} item={item} />
            ))}
          </Timeline>
        </Fold>
      ) : null}
    </>
  )
}

/* -------------------------------------------------------------------- one row */

function Row({ item }: { readonly item: ClientUpcomingItemView }) {
  const format = useFormat()
  // Unconditionally: `abilitiesOf(usePrincipal())` behind a `||` is a hook behind a
  // short circuit, which `react-hooks/rules-of-hooks` refuses and is right to.
  const abilities = abilitiesOf(usePrincipal())
  const mayFollow = !NEEDS_REQUESTS.includes(item.link.kind) || abilities.raisesRequests
  /*
   * The day is rendered in **UTC** from noon, and the time is not.
   *
   * `on` is a `DateOnly` the read model already resolved into the viewer's zone
   * (`ClientUpcomingSchema`), so re-resolving it would move it a second time —
   * which is exactly what `format.day` does and why it exists. `at` is an instant
   * and is the viewer's to read, which is what `clock` prints with its zone beside
   * it.
   */
  const day = format.day(item.on, 'weekday')
  const when = item.at === null ? day : `${day} · ${format.date(item.at, 'clock')}`

  return (
    <TimelineItem
      need={item.need}
      date={when}
      title={item.title}
      note={
        <>
          {/*
            The note is "what this is" and the link word is `LINK_TEXT`'s.
            `readmodels/client-upcoming.ts` used to end each note with its own link
            word, so every row printed it twice — "… · the archive · the archive" —
            and a content drop with no channel on it printed "Scheduled · " and then
            another separator. Fixed there, in the one place that composes a note.
          */}
          {item.note === '' ? null : <>{item.note} · </>}
          {mayFollow ? (
            <Linkish asChild>
              <Link to={LINK_PATH[item.link.kind]} {...hashFor(item.link)}>
                {LINK_TEXT[item.link.kind]}
              </Link>
            </Linkish>
          ) : (
            LINK_TEXT[item.link.kind]
          )}
        </>
      }
    />
  )
}

/**
 * Two items can fall on one day — a content drop and a call — so the day alone is
 * not a key. The title is what makes them different rows.
 */
function rowKey(item: ClientUpcomingItemView): string {
  return `${item.on}:${item.title}`
}
